// Libraries
import { useEffect, useRef, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// @mui material components
import Grid from "@mui/material/Grid2";
import Card from "@mui/material/Card";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// Redux Actions
import setAlert from "store/actions/alertActions";

// Layouts
import DashboardLayout from "layouts/DashboardLayout";

// Custom Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardNavbar from "components/DashboardNavbar";
import Footer from "components/Footer";
import { scrollToTop } from "components/helper";

// Schema
import {
  addDestination,
  setDestinationsLoading,
  clearDestinationsErrors,
} from "store/actions/destinationsActions";
import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";
import Address from "./components/Address";
import Review from "./components/Review";
import Information from "./components/Information";

const mainArray = ["Information", "Address", "Review"];

function getSteps() {
  return ["Information", "Address", "Review"];
}

function getStepContent(
  stepIndex,
  formData,
  selectedParent,
  handleParentChange,
  handleCurrencyChange,
  selectedCurrency,
  selectedTimeZone,
  handleTimeZoneChange
) {
  if (formData) {
    switch (mainArray[stepIndex]) {
      case "Information":
        return (
          <Information
            formData={formData}
            selectedParent={selectedParent}
            handleParentChange={handleParentChange}
          />
        );
      case "Address":
        return (
          <Address
            formData={formData}
            handleCurrencyChange={handleCurrencyChange}
            selectedCurrency={selectedCurrency}
            selectedTimeZone={selectedTimeZone}
            handleTimeZoneChange={handleTimeZoneChange}
          />
        );
      case "Review":
        return <Review formData={formData} />;
      default:
        return null;
    }
  }
  return null;
}

const NewDestination = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // selector
  const user = useSelector((state) => state.users.user);
  const error = useSelector((state) => state.locations.error);

  const handleValidation = (activeStep) => {
    switch (mainArray[activeStep]) {
      case "Information":
        return validations[0];
      case "Address":
        return validations[1];
      default:
        return null;
    }
  };

  // state
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [selectedParent, setSelectedParent] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState({});

  const { formId, formField } = form;
  const [formChange, setFormChange] = useState(null);
  const currentValidation = handleValidation(activeStep);
  const isLastStep = activeStep === steps.length - 1;

  useEffect(() => {
    setSteps(getSteps("admin"));
  }, []);
  useEffect(() => {
    setSelectedCurrency(formChange?.defaultCurrencyCode);
    setSelectedTimeZone(formChange?.timeZone);
  }, [formChange?.timeZone, formChange?.defaultCurrencyCode]);

  useEffect(() => {
    if (error) {
      dispatch(setAlert(error, "error"));
      dispatch(clearDestinationsErrors());
    }
  }, [error, setAlert, clearDestinationsErrors]);

  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  const handleBack = () => {
    setActiveStep(activeStep - 1);
    scrollToTop(); // Scroll to the top
  };

  const handleNavigation = (type) => {
    let url = null;
    if (type === "admin") {
      url = `/admin/destinations?`;
      navigate(url);
    } else if (type === "partner") {
      url = `/partner/destinations`;
      navigate(url);
    }
  };

  const submitForm = async (values, actions) => {
    await sleep(1000);
    dispatch(setDestinationsLoading());
    // eslint-disable-next-line no-alert
    dispatch(addDestination(values, values?.logo));
    actions.setSubmitting(false);
    actions.resetForm();
    setActiveStep(0);
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
      handleNavigation(user?.role);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
      scrollToTop(); // Scroll to the top
    }
  };

  const handleParentChange = (val) => {
    if (val) {
      setSelectedParent(val);
    }
  };

  const handleCurrencyChange = (val) => {
    if (val) {
      setSelectedCurrency(val);
    }
  };
  const handleTimeZoneChange = (val) => {
    if (val) {
      setSelectedTimeZone(val);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} mb={20}>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%", mt: 8 }}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Formik
              initialValues={initialValues}
              enableReinitialize
              validationSchema={steps[activeStep] === "Review" ? undefined : currentValidation}
              onSubmit={handleSubmit}
              innerRef={(f) => {
                setFormChange(f?.values);
                formRef.current = f;
              }}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <MDBox mx={2} mt={-3}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </MDBox>
                    <MDBox p={3}>
                      <MDBox>
                        {getStepContent(
                          activeStep,
                          {
                            values,
                            touched,
                            formField,
                            errors,
                            setFieldValue,
                          },
                          selectedParent,
                          handleParentChange,
                          handleCurrencyChange,
                          selectedCurrency,
                          selectedTimeZone,
                          handleTimeZoneChange
                        )}
                        <MDBox mt={2} width="100%" display="flex" justifyContent="space-between">
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton variant="gradient" color="light" onClick={handleBack}>
                              back
                            </MDButton>
                          )}
                          <div>
                            <MDButton
                              onClick={() => handleNavigation(user?.role)}
                              rel="noreferrer"
                              variant="outlined"
                              color="dark"
                              sx={{ marginRight: "8px" }}
                            >
                              Cancel
                            </MDButton>
                            <MDButton
                              disabled={isSubmitting}
                              type="submit"
                              variant="gradient"
                              color="dark"
                            >
                              {isLastStep ? "add" : "next"}
                            </MDButton>
                          </div>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewDestination;
